import { Container, Box, Alert, Button, CircularProgress } from "@mui/material";
import { useTaskerMessageContext } from ".";
import { RewardOptionCard } from "./RewardOptionCard";
import { RejectOptionCard } from "./RejectOptionCard";
import { useState, useEffect, useMemo } from "react";
import {
  TaskerRewardOption,
  TaskerRejectOption,
  isCustomRejectOption,
} from "@t3/shared";
import { ProgramReviewCard } from "./ProgramReviewCard";
import { TaskerMessageReviewCard } from "./TaskerMessageReviewCard";
import { ReviewHeader } from "./ReviewHeader";
import { ReviewConfirmationDialog } from "./ReviewConfirmationDialog";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { firestore } from "@/firebase";
import { doc } from "firebase/firestore";
import { useCompanyConfigContext } from "@/context/CompanyConfigContext";
import { taskerMessageConverter } from "@/converters";

type MyViewType = "reward" | "reject";

export const TaskerMessageReview = () => {
  const { company } = useCompanyConfigContext();
  const {
    taskerAssignedMessages,
    updateTaskerMessage,
    managerTaskerConfigs,
    loading,
    error,
  } = useTaskerMessageContext();
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);

  const currentMessageRef = useMemo(() => {
    if (currentMessageId !== null) {
      return doc(
        firestore,
        `company/${company.id}/tasker/${currentMessageId}`,
      ).withConverter(taskerMessageConverter);
    }
    return null;
  }, [currentMessageId, company.id]);

  const [selectedOptions, setSelectedOptions] = useState<TaskerRewardOption[]>(
    [],
  );
  const [selectedRejectReasons, setSelectedRejectReasons] = useState<
    TaskerRejectOption[]
  >([]);
  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<Boolean>(false);
  const [viewMode, setViewMode] = useState<MyViewType>("reward");

  const [currentMessage, _currentMessageLoading, _currentMessageError] =
    useDocumentData(currentMessageRef);

  useEffect(() => {
    if (!currentMessageId && taskerAssignedMessages.length > 0) {
      setCurrentMessageId(taskerAssignedMessages[0].id);
    }
  }, [taskerAssignedMessages, currentMessageId]);

  useEffect(() => {
    if (currentMessage) {
      setSelectedOptions(currentMessage.rewardOptions || []);
      setSelectedRejectReasons(currentMessage.rejectOptions || []);
      setViewMode(
        currentMessage.reviewStatus === "rejected" ? "reject" : "reward",
      );
    }
  }, [currentMessage?.id]);

  const handleRejectReasonSelect = (reason: TaskerRejectOption | null) => {
    if (!reason) return;

    setSelectedRejectReasons((prev) => {
      if (isCustomRejectOption(reason)) {
        const filtered = prev.filter((r) => r.id !== reason.id);
        return reason.message ? [...filtered, reason] : filtered;
      }

      const isSelected = prev.some((r) => r.id === reason.id);
      return isSelected
        ? prev.filter((r) => r.id !== reason.id)
        : [...prev, reason];
    });
  };

  const submit = async (type: MyViewType) => {
    if (!currentMessage?.id) return;

    const nextMessageId = taskerAssignedMessages[1]?.id ?? null;

    try {
      if (type === "reward") {
        await updateTaskerMessage(currentMessage.id, {
          rewardOptions: selectedOptions,
          reviewStatus: "approved",
        });
      } else {
        await updateTaskerMessage(currentMessage.id, {
          rejectOptions: selectedRejectReasons,
          reviewStatus: "rejected",
        });
      }

      setConfirmationDialogOpen(true);

      setTimeout(() => {
        setConfirmationDialogOpen(false);
        setCurrentMessageId(nextMessageId);
      }, 3000);
    } catch (error) {
      console.error("Error updating message:", error);
    }
  };

  const handleOptionSelect = (option: TaskerRewardOption) => {
    setSelectedOptions((prev) => {
      const isSelected = prev.some((s) => s.id === option.id);
      return isSelected
        ? prev.filter((s) => s.id !== option.id)
        : [...prev, option];
    });
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Error loading messages: {error.message}</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!currentMessage) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">No pending messages to review.</Alert>
      </Container>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          background: "#f5f7fa",
        }}
      >
        <Container maxWidth="lg" sx={{ px: 0 }}>
          <ReviewHeader />

          <Box
            sx={{
              minHeight: "400px",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, sm: 2.5 },
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            <Box sx={{ width: { xs: "100%", md: "50%" } }}>
              <TaskerMessageReviewCard
                key={currentMessage.id}
                message={currentMessage}
              />
            </Box>

            <Box sx={{ width: { xs: "100%", md: "70%" } }}>
              <Box
                sx={{
                  pr: { xs: 1, sm: 1.5 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  {viewMode === "reward" ? (
                    <>
                      {managerTaskerConfigs
                        .filter((config) =>
                          currentMessage?.eligibleProgramIds.includes(
                            config.programId,
                          ),
                        )
                        .map((config) => (
                          <ProgramReviewCard
                            key={`${config.programId}-${config.id}`}
                            programName={config.programName}
                            programColor={config.programColor}
                          >
                            {config.rewardOptions?.map((option, index) => (
                              <RewardOptionCard
                                key={index}
                                option={option}
                                isSelected={selectedOptions.some(
                                  (s) => s.id === option.id,
                                )}
                                onSelect={handleOptionSelect}
                                disabled={
                                  currentMessage?.reviewStatus !== "pending"
                                }
                                programColor="#ffffff"
                              />
                            ))}
                          </ProgramReviewCard>
                        ))}
                    </>
                  ) : (
                    <>
                      {managerTaskerConfigs
                        .filter((config) =>
                          currentMessage?.eligibleProgramIds.includes(
                            config.programId,
                          ),
                        )
                        .map((config) => (
                          <ProgramReviewCard
                            key={`${config.programId}-${config.id}`}
                            programName={config.programName}
                            programColor={config.programColor}
                          >
                            {!config.rejectOptions ||
                            config.rejectOptions.length === 0 ? (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                No reject options available for this program
                              </Alert>
                            ) : (
                              config.rejectOptions.map((option) => (
                                <RejectOptionCard
                                  key={option.id}
                                  option={option}
                                  isSelected={selectedRejectReasons.some(
                                    (r) => r.id === option.id,
                                  )}
                                  onSelect={handleRejectReasonSelect}
                                />
                              ))
                            )}
                          </ProgramReviewCard>
                        ))}
                    </>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1.5, sm: 2 },
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    {currentMessage?.reviewStatus === "pending" && (
                      <>
                        <Button
                          variant={
                            viewMode === "reward" ? "contained" : "outlined"
                          }
                          color="error"
                          onClick={() =>
                            setViewMode(
                              viewMode === "reward" ? "reject" : "reward",
                            )
                          }
                          data-cypress={`${viewMode === "reward" ? "reject-button-toggle" : "review-button-toggle"}`}
                          sx={{
                            flex: { xs: "1 1 100%", sm: "0 1 auto" },
                            minWidth: { xs: "auto", sm: 130 },
                            py: { xs: 1.25, sm: 1.5 },
                            fontWeight: 700,
                            textTransform: "none",
                            fontSize: { xs: "0.875rem", sm: "0.95rem" },
                            boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                          }}
                        >
                          {viewMode === "reward" ? "Reject" : "Cancel Reject"}
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          disabled={
                            viewMode === "reward"
                              ? selectedOptions.length === 0
                              : selectedRejectReasons.length === 0
                          }
                          onClick={() => submit(viewMode)}
                          data-cypress="review-save-continue-button"
                          sx={{
                            flex: { xs: "1 1 100%", sm: "0 1 auto" },
                            minWidth: { xs: "auto", sm: 130 },
                            py: { xs: 1.25, sm: 1.5 },
                            fontWeight: 700,
                            textTransform: "none",
                            fontSize: { xs: "0.875rem", sm: "0.95rem" },
                            boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                          }}
                        >
                          Save & Continue
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {confirmationDialogOpen && (
        <ReviewConfirmationDialog currentMessage={currentMessage} />
      )}
    </>
  );
};
